import {
  ActionPattern,
  all,
  call,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects'
import { parse } from 'query-string'
import { Saga } from 'redux-saga'
import DOMPurify from 'isomorphic-dompurify'

import ApiSagas from '../api/sagas'
import { actions, selectors } from '../'
import * as Events from '../../graphql/Services/Events'
import { Department } from '../../types/fam-graphql'

const PER_PAGE = 6

export default class EventLandingSagas {
  static *init(context: any): any {
    const {
      query: { page = 1 },
    } = context

    const sanitizedPage = DOMPurify.sanitize(page)

    const toBeParsed =
      context?.asPath?.split('?')?.pop() ??
      context?.req?.url?.split('?')?.pop() ??
      ''

    const qs = parse(toBeParsed, { arrayFormat: 'comma' })

    const filters = {
      query: qs?.query ?? undefined,
      paid: qs?.paid ? (Array.isArray(qs?.paid) ? qs?.paid : [qs?.paid]) : [],
      weeks: qs?.weeks
        ? Array.isArray(qs?.weeks)
          ? qs?.weeks
          : [qs?.weeks]
        : [],
      types: qs?.types
        ? Array.isArray(qs?.types)
          ? qs?.types
          : [qs?.types]
        : [],
      departments: qs?.departments
        ? Array.isArray(qs?.departments)
          ? qs?.departments
          : [qs?.departments]
        : [],
      places: qs?.places
        ? Array.isArray(qs?.places)
          ? qs?.places
          : [qs?.places]
        : [],
      // durations: qs?.durations
      //   ? Array.isArray(qs?.durations)
      //     ? qs?.durations
      //     : [qs?.durations]
      //   : [],
      modes: qs?.modes
        ? Array.isArray(qs?.modes)
          ? qs?.modes
          : [qs?.modes]
        : [],
      // targets: qs?.targets
      //   ? Array.isArray(qs?.targets)
      //     ? qs?.targets
      //     : [qs?.targets]
      //   : [],
    }

    yield put(actions.eventLanding.setEventsFilters(filters))
    yield put(actions.eventLanding.setEventsPage(parseInt(sanitizedPage, 10)))

    yield call(EventLandingSagas.loadFilterOptions)
    yield call(EventLandingSagas.loadEvents)
  }

  static *getFilters() {
    const apiFilters = yield select(selectors.eventLanding.apiFilters)
    return {
      query: apiFilters?.query ?? null,
      paid:
        apiFilters?.paid?.[0] === 'true'
          ? true
          : apiFilters?.paid?.[0] === 'false'
          ? false
          : null,
      weeks: apiFilters?.weeks?.length > 0 ? apiFilters?.weeks : null,
      types: apiFilters?.types?.length > 0 ? apiFilters?.types : null,
      departments:
        apiFilters?.departments?.length > 0 ? apiFilters?.departments : null,
      places: apiFilters?.places?.length > 0 ? apiFilters?.places : null,
      // durations:
      //   apiFilters?.durations?.length > 0 ? apiFilters?.durations : null,
      modes: apiFilters?.modes?.length > 0 ? apiFilters?.modes : null,
      // targets: apiFilters?.targets?.length > 0 ? apiFilters?.targets : null,
    }
  }

  static *loadEvents() {
    const page = yield select(selectors.eventLanding.page)
    const filters = yield call(EventLandingSagas.getFilters)
    const eventDates = yield select(selectors.eventLanding.eventDates)

    const newFilters = {
      ...filters,
      weeks:
        filters?.weeks?.length > 0
          ? filters?.weeks.map((w: number) => ({
              start: eventDates[w].start,
              end: eventDates[w].end,
            }))
          : null,
    }

    const { data: result } = yield call(
      ApiSagas.query,
      Events.queries.events({
        first: page * PER_PAGE,
        page: 1,
        ...newFilters,
      })
    )

    yield put(
      actions.eventLanding.setHasMorePages(result.paginatorInfo.hasMorePages)
    )

    yield put(actions.eventLanding.setEvents(result?.data))

    if (!result) {
      return { error: 404 }
    }
  }

  static *loadFilterOptions() {
    const departments = yield select(selectors.eventLanding.departments)
    if (!departments?.length) {
      const result = yield call(ApiSagas.query, Events.queries.filters())

      yield put(
        actions.eventLanding.setDepartments(
          result?.data?.departments?.sort((a: Department, b: Department) =>
            (a?.code ?? 0) < (b?.code ?? 0) ? -1 : 1
          )
        )
      )
      yield put(
        actions.eventLanding.setEventAccessibilityModes(
          result?.data?.eventAccessibilityModes
        )
      )
      yield put(
        actions.eventLanding.setEventActivityTypes(
          result?.data?.eventActivityTypes
        )
      )
      // yield put(
      //   actions.eventLanding.setEventTargets(result?.data?.eventTargets)
      // )
      yield put(actions.eventLanding.setEventDates(result?.data?.eventDates))
    }
  }

  static *loadMoreEvents() {
    const page = yield select(selectors.eventLanding.page)
    const newPage = page + 1
    const filters = yield call(EventLandingSagas.getFilters)

    const { data: result } = yield call(
      ApiSagas.query,
      Events.queries.events({
        first: PER_PAGE,
        page: newPage,
        ...filters,
      })
    )

    yield put(
      actions.eventLanding.setHasMorePages(result.paginatorInfo.hasMorePages)
    )

    yield put(actions.eventLanding.setEventsPage(newPage))
    yield put(actions.eventLanding.appendEvents(result?.data))

    if (!result) {
      return { error: 404 }
    }
  }

  static *loop() {
    yield all([
      takeLatest(
        actions.eventLanding.loadMoreEventsPage as ActionPattern,
        this.loadMoreEvents as Saga
      ),
      takeLatest(
        actions.eventLanding.loadEvents as ActionPattern,
        this.loadEvents as Saga
      ),
    ])
  }
}
